import React, { useCallback, useEffect, useState } from 'react';
import { appFunnelAPI } from '../services/api';
import { FiRefreshCw, FiSmartphone } from 'react-icons/fi';
import './AppFunnel.css';

const RANGES = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
];

const INTENT_LABEL = { STUDY: 'Study', WORK: 'Work', TRAVEL: 'Travel', UNSURE: 'Not sure' };
const CHANNEL_LABEL = { CONSULTATION: 'Consultation', CALLBACK: 'WhatsApp callback', QUIZ: 'Eligibility quiz' };
const STATUS_LABEL = { NEW: 'New', CONTACTED: 'Contacted', CONVERTED: 'Converted', DISMISSED: 'Dismissed' };

function pct(n) {
  return `${Math.round((n || 0) * 100)}%`;
}

export default function AppFunnel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await appFunnelAPI.get(days);
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load the funnel.');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const top = data?.funnel?.[0]?.value || 0;

  return (
    <div className="appfunnel">
      <div className="appfunnel-head">
        <h1><FiSmartphone /> App Funnel</h1>
        <div className="appfunnel-controls">
          <div className="appfunnel-ranges">
            {RANGES.map((r) => (
              <button
                key={r.days}
                className={days === r.days ? 'active' : ''}
                onClick={() => setDays(r.days)}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button className="appfunnel-refresh" onClick={load} disabled={loading}>
            <FiRefreshCw className={loading ? 'spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {error && <div className="appfunnel-error">{error}</div>}

      {data && (
        <>
          <p className="appfunnel-muted">
            Trailing {data.range.days} days · anonymous browsing and captured leads from the
            VisionExploit app. Steps are independent counts, not strict subsets.
          </p>

          <div className="appfunnel-steps">
            {data.funnel.map((s, i) => {
              const width = top ? Math.max(4, Math.round((s.value / top) * 100)) : 4;
              const prev = i > 0 ? data.funnel[i - 1].value : null;
              const drop = prev ? pct(s.value / prev) : null;
              return (
                <div className="appfunnel-step" key={s.key}>
                  <div className="appfunnel-step-label">{s.label}</div>
                  <div className="appfunnel-bar-track">
                    <div className="appfunnel-bar" style={{ width: `${width}%` }}>
                      <span className="appfunnel-bar-value">{s.value.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="appfunnel-step-drop">{drop ? `${drop} of prev` : ''}</div>
                </div>
              );
            })}
          </div>

          <div className="appfunnel-stats">
            <Stat label="Engaged → lead" value={pct(data.conversion.engaged_to_lead)} />
            <Stat label="Lead → converted" value={pct(data.conversion.lead_to_signed)} />
            <Stat label="App sign-ups" value={(data.signups || 0).toLocaleString()} />
          </div>

          <div className="appfunnel-breakdowns">
            <Breakdown title="Leads by intent" data={data.by_intent} labels={INTENT_LABEL} />
            <Breakdown title="Leads by channel" data={data.by_channel} labels={CHANNEL_LABEL} />
            <Breakdown title="Lead status" data={data.lead_status} labels={STATUS_LABEL} />
          </div>
        </>
      )}

      {loading && !data && <div className="appfunnel-muted">Loading…</div>}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="appfunnel-stat">
      <div className="appfunnel-stat-value">{value}</div>
      <div className="appfunnel-stat-label">{label}</div>
    </div>
  );
}

function Breakdown({ title, data, labels }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((s, [, n]) => s + n, 0);
  return (
    <div className="appfunnel-breakdown">
      <h3>{title}</h3>
      {entries.length === 0 && <div className="appfunnel-muted">No leads yet.</div>}
      {entries.map(([k, n]) => (
        <div className="appfunnel-brow" key={k}>
          <span>{labels[k] || k}</span>
          <span className="appfunnel-bnum">
            {n}
            <span className="appfunnel-bpct"> · {total ? Math.round((n / total) * 100) : 0}%</span>
          </span>
        </div>
      ))}
    </div>
  );
}
